// Small helper for persisted table view state (sort + filter + search).
// Column filters STACK across columns (AND) — verified + date + name can all
// narrow the list at once. Sorting stacks ON TOP of filters: it reorders the
// filtered rows and never clears them. Global search always stacks on top.
import { useCallback, useEffect, useMemo, useState } from "react";

const DEFAULT = { sortKey: null, sortDir: null, filters: {}, search: "" };

export function useTableView(storageKey) {
  const [view, setView] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Back-compat: migrate the old single-filter shapes to the map.
        if (parsed && parsed.filters == null) {
          if (parsed.filterKey && Array.isArray(parsed.filterValues) && parsed.filterValues.length > 0) {
            parsed.filters = { [parsed.filterKey]: parsed.filterValues.map(String) };
          } else if (parsed.filterKey && parsed.filterValue != null) {
            parsed.filters = { [parsed.filterKey]: [String(parsed.filterValue)] };
          } else {
            parsed.filters = {};
          }
        }
        delete parsed.filterKey;
        delete parsed.filterValues;
        delete parsed.filterValue;
        return { ...DEFAULT, ...parsed };
      }
    } catch (e) { /* ignore */ }
    return DEFAULT;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(view));
    } catch (e) { /* ignore */ }
  }, [storageKey, view]);

  // Merge patch; rules:
  //  - Sort and column filters COEXIST: sorting reorders the filtered rows,
  //    and changing a filter never touches the active sort.
  //  - Column filters stack: a { filterKey, filterValues } patch only
  //    touches that column's entry in the filters map.
  //  - Multi-value filtering is allowed within a single column (OR);
  //    across columns the filters combine (AND).
  //  - Global search is untouched by sort/filter changes.
  const update = useCallback((patch) => {
    setView((prev) => {
      const next = { ...prev };
      if (patch.sortKey !== undefined) {
        next.sortKey = patch.sortKey;
        next.sortDir = patch.sortDir ?? null;
      }
      if (patch.filterKey !== undefined) {
        const f = { ...(prev.filters || {}) };
        if (Array.isArray(patch.filterValues) && patch.filterValues.length > 0) {
          f[patch.filterKey] = patch.filterValues.map((v) => String(v));
        } else {
          delete f[patch.filterKey];
        }
        next.filters = f;
      }
      if (patch.filters !== undefined && patch.filterKey === undefined) {
        next.filters = patch.filters || {};
      }
      if (patch.search !== undefined) next.search = patch.search;
      return next;
    });
  }, []);

  const reset = useCallback(() => setView(DEFAULT), []);

  return [view, update, reset];
}

/** True when any column filter is active. */
export function hasActiveFilters(view) {
  return Object.values(view?.filters || {}).some((v) => Array.isArray(v) && v.length > 0);
}

/**
 * Apply search → filters (stacked) → sort to a row list.
 *   columns: {
 *     [key]: {
 *       get(row): string|number,          // raw value (used for filter + sort)
 *       displayGet?(row): string,         // human-friendly text (used for search)
 *       type?: "text"|"date"|"number"
 *     }
 *   }
 *   searchKeys: string[] — keys to search across for the global search bar.
 */
export function applyTableView(rows, view, columns, searchKeys) {
  const q = (view.search || "").trim().toLowerCase();
  let out = rows;

  if (q) {
    out = out.filter((r) =>
      searchKeys.some((k) => {
        const col = columns[k];
        if (!col) return false;
        const raw = col.get(r);
        const disp = col.displayGet ? col.displayGet(r) : null;
        return [raw, disp].some(
          (v) => v != null && String(v).toLowerCase().includes(q)
        );
      })
    );
  }

  // Every active column filter must match (AND across columns, OR within one).
  for (const [key, values] of Object.entries(view.filters || {})) {
    const col = columns[key];
    if (!col || !Array.isArray(values) || values.length === 0) continue;
    const targets = new Set(values.map((v) => (v == null ? "" : String(v))));
    out = out.filter((r) => {
      const v = col.get(r);
      return targets.has(v == null ? "" : String(v));
    });
  }

  if (view.sortKey && columns[view.sortKey]) {
    const col = columns[view.sortKey];
    const dir = view.sortDir === "desc" ? -1 : 1;
    out = [...out].sort((a, b) => {
      const av = col.get(a);
      const bv = col.get(b);
      if (col.type === "date" || col.type === "number") {
        const an = col.type === "date" ? dateNum(av) : num(av);
        const bn = col.type === "date" ? dateNum(bv) : num(bv);
        return (an - bn) * dir;
      }
      const as = (av == null ? "" : String(av)).toLowerCase();
      const bs = (bv == null ? "" : String(bv)).toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  }

  return out;
}

function dateNum(v) {
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Extract unique, sorted values for a column.
 */
export function uniqueValues(rows, getter) {
  const set = new Set();
  rows.forEach((r) => {
    const v = getter(r);
    if (v != null && v !== "") set.add(String(v));
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Extract unique {value, label} pairs. Label is what the dropdown shows;
 * value is what the filter matches against.
 */
export function uniqueValuePairs(rows, getter, labeler) {
  const map = new Map();
  rows.forEach((r) => {
    const v = getter(r);
    if (v == null || v === "") return;
    const key = String(v);
    if (!map.has(key)) map.set(key, labeler ? labeler(r) : key);
  });
  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
